import type { CellObject, WorkBook, WorkSheet } from 'xlsx'
import type { FieldDef, SheetDef } from '@/lib/data/schema'
import type { Tokens } from '@/lib/data/normalize'

import type {
    Agreement,
    BasicControl,
    BudgetRow,
    BudgetSource,
    ComplianceItem,
    CsfItem,
    Dataset,
    DesignActivity,
    Finding,
    HistoryRow,
    Initiative,
    InitiativeStatus,
    Params,
    Period,
    QualityReport,
    Risk,
    SheetKey,
    SheetReport,
    TierAsset,
    VolumetryRow
} from '@/lib/data/types'

import { PARAM_KEYS, SHEETS } from '@/lib/data/schema'

import {
    clamp,
    cleanText,
    isPercentText,
    joinKey,
    matchScore,
    normalizeValue,
    parseDate,
    parseNumber,
    parsePeriod,
    toAgreementStatus,
    toComplianceStatus,
    toCriticality,
    toCsfFunction,
    toCurrency,
    toDirection,
    toFindingStatus,
    toInitiativeStatus,
    toRag,
    toRiskStatus,
    toTier,
    tokenize
} from '@/lib/data/normalize'

/* ------------------------------------------------------------------ */
/* Load limits. Any breach rejects the file with one generic message.  */
/* ------------------------------------------------------------------ */

const MAX_BYTES = 15 * 1024 * 1024
const MAX_SHEETS = 40
const MAX_ROWS = 20000
const MAX_COLS = 150
const HEADER_SCAN_ROWS = 10
const ALLOWED_TYPES = ['', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/octet-stream']

/** Workbook parts that are never accepted: macros, external links, embedded objects, data connections. */
const FORBIDDEN_PARTS = [/vbaproject/i, /externallinks?\//i, /activex/i, /embeddings\//i, /oleobject/i, /customui/i, /connections\.xml$/i, /querytables\//i, /vbadata/i]

export type LoadResult =
    | { ok: true, dataset: Dataset, workbook: WorkBook }
    | { ok: false }

const FAIL: LoadResult = { ok: false }

/* ------------------------------------------------------------------ */
/* Header and sheet recognition                                        */
/* ------------------------------------------------------------------ */

type Mapping = Map<string, number>

interface Table {
    name: string
    aoa: unknown[][]
    /** Tokenized cells of the first rows, where the header is searched */
    head: Tokens[][]
    /** Top-left cell of the sheet range */
    origin: { r: number, c: number }
}

const tokenCache = new Map<string, Tokens>()

const synonymTokens = (synonym: string): Tokens => {
    const cached = tokenCache.get(synonym)

    if (cached) return cached

    const tokens = tokenize(synonym)

    tokenCache.set(synonym, tokens)

    return tokens
}

/** Greedy assignment of columns to fields, best scores first. */
const mapHeaders = (fields: FieldDef[], headers: Tokens[]): Mapping => {
    const candidates: { field: string, column: number, score: number }[] = []

    headers.forEach((tokens, column) => {
        if (!tokens.list.length) return

        for (const field of fields) {
            let best = 0

            for (const synonym of field.synonyms) {
                const score = matchScore(tokens, synonymTokens(synonym))

                if (score > best) best = score
            }

            if (best > 0) candidates.push({ field: field.key, column, score: best })
        }
    })

    candidates.sort((a, b) => b.score - a.score || a.column - b.column)

    const mapping: Mapping = new Map()
    const usedColumns = new Set<number>()

    for (const candidate of candidates) {
        if (mapping.has(candidate.field) || usedColumns.has(candidate.column)) continue

        mapping.set(candidate.field, candidate.column)
        usedColumns.add(candidate.column)
    }

    return mapping
}

/** The header row is the one (among the first rows) that maps the most fields. */
const findHeader = (def: SheetDef, table: Table): { row: number, mapping: Mapping } | null => {
    let best: { row: number, mapping: Mapping } | null = null

    table.head.forEach((cells, row) => {
        const mapping = mapHeaders(def.fields, cells)

        if (!best || mapping.size > best.mapping.size) best = { row, mapping }
    })

    const found = best as { row: number, mapping: Mapping } | null

    if (!found || found.mapping.size < 2) return null

    return found
}

const nameScore = (def: SheetDef, sheetName: Tokens): number => {
    let best = 0

    for (const synonym of def.synonyms) {
        const score = matchScore(sheetName, synonymTokens(synonym))

        if (score > best) best = score
    }

    return best
}

/** How many identifying columns of a sheet type appear in the table header. */
const fingerprintScore = (def: SheetDef, table: Table): number => {
    const fields = def.fields.filter(field => def.fingerprint.includes(field.key))
    let best = 0

    for (const cells of table.head) {
        best = Math.max(best, mapHeaders(fields, cells).size)
    }

    return best
}

/* ------------------------------------------------------------------ */
/* Row access                                                          */
/* ------------------------------------------------------------------ */

interface Row {
    index: number
    has: (field: string) => boolean
    raw: (field: string) => unknown
    text: (field: string) => string
    num: (field: string) => number | null
    pct: (field: string) => number | null
    date: (field: string) => number | null
    period: (field: string) => Period | null
}

interface SheetRows {
    def: SheetDef
    sheetName: string
    aoa: unknown[][]
    headerRow: number
    mapping: Mapping
    rows: Row[]
    rowsRead: number
}

const isEmptyCell = (value: unknown): boolean => value === null || value === undefined || (typeof value === 'string' && value.trim() === '')

const buildRows = (def: SheetDef, sheetName: string, aoa: unknown[][], headerRow: number, mapping: Mapping, date1904: boolean): SheetRows => {
    // Percent columns: values stored as fractions (0.45) are scaled to 45
    const fraction = new Set<string>()

    for (const field of def.fields) {
        if (field.type !== 'percent' || !mapping.has(field.key)) continue

        const column = mapping.get(field.key) as number
        let numbers = 0
        let small = 0
        let positive = 0

        for (let index = headerRow + 1; index < aoa.length; index++) {
            const cell = aoa[index]?.[column]

            if (isEmptyCell(cell) || isPercentText(cell)) continue

            const value = parseNumber(cell)

            if (value === null) continue

            numbers++
            if (Math.abs(value) <= 1) small++
            if (value > 0) positive++
        }

        if (numbers > 0 && small === numbers && positive > 0) fraction.add(field.key)
    }

    const rows: Row[] = []
    let rowsRead = 0

    for (let index = headerRow + 1; index < aoa.length; index++) {
        const cells = aoa[index] ?? []
        const mappedCells = [...mapping.values()].map(column => cells[column])

        if (mappedCells.every(isEmptyCell)) continue

        rowsRead++

        const raw = (field: string): unknown => {
            const column = mapping.get(field)

            return column === undefined ? null : cells[column]
        }

        rows.push({
            index,
            has: field => mapping.has(field),
            raw,
            text: field => cleanText(raw(field)),
            num: field => parseNumber(raw(field)),
            pct: field => {
                const cell = raw(field)
                const value = parseNumber(cell)

                if (value === null) return null
                if (isPercentText(cell)) return value

                return fraction.has(field) ? value * 100 : value
            },
            date: field => parseDate(raw(field), date1904),
            period: field => parsePeriod(raw(field), date1904)
        })
    }

    return { def, sheetName, aoa, headerRow, mapping, rows, rowsRead }
}

const hasValue = (row: Row, field: string): boolean => {
    if (!row.has(field)) return false

    return !isEmptyCell(row.raw(field))
}

/** Rows that satisfy every required group of the sheet. */
const validRows = (sheet: SheetRows): Row[] => {
    return sheet.rows.filter(row => sheet.def.rowRequires.every(group => group.some(field => hasValue(row, field))))
}

const pct = (value: number | null): number | null => (value === null ? null : clamp(value, 0, 100))

const inferStatus = (progress: number | null): InitiativeStatus => {
    if (progress === null || progress <= 0) return 'backlog'
    if (progress >= 100) return 'closed'

    return 'active'
}

const scale5Words: [number, string[]][] = [
    [5, ['muy alta', 'muy alto', 'critica', 'critico', 'casi seguro', 'very high', 'extrema', 'extremo', 'catastrofico']],
    [1, ['muy baja', 'muy bajo', 'rara', 'raro', 'very low', 'insignificante', 'minima', 'minimo']],
    [4, ['alta', 'alto', 'high', 'probable', 'mayor', 'importante']],
    [3, ['media', 'medio', 'medium', 'moderada', 'moderado', 'posible']],
    [2, ['baja', 'bajo', 'low', 'improbable', 'menor']]
]

/** Probability and impact on a 1 to 5 scale, from a number or a word. */
const toScale5 = (input: unknown): number | null => {
    const value = parseNumber(input)

    if (value !== null && typeof input !== 'string') return clamp(Math.round(value), 1, 5)
    if (value !== null && /^\s*\d+([.,]\d+)?\s*$/.test(String(input))) return clamp(Math.round(value), 1, 5)

    const text = normalizeValue(input)

    if (!text) return null

    for (const [score, words] of scale5Words) {
        if (words.includes(text)) return score
    }

    for (const [score, words] of scale5Words) {
        if (words.some(word => ` ${text} `.includes(` ${word} `))) return score
    }

    return null
}

const uniqueKeys = <T extends { key: string }>(items: T[]): T[] => {
    const seen = new Map<string, number>()

    return items.map(item => {
        const base = item.key || 'row'
        const count = seen.get(base) ?? 0

        seen.set(base, count + 1)

        return count === 0 && item.key ? item : { ...item, key: `${base}#${count + 1}` }
    })
}

const dimsOf = (row: Row) => ({
    division: row.text('division'),
    bu: row.text('bu'),
    owner: row.text('owner'),
    program: row.text('program'),
    domain: row.text('domain'),
    criticality: toCriticality(row.raw('criticality'))
})

/* ------------------------------------------------------------------ */
/* Sheet builders                                                      */
/* ------------------------------------------------------------------ */

const buildParams = (rows: Row[]): Params => {
    const params: Params = { exchangeRate: null, baseCurrency: null, cutDate: null, amberPp: null, redPp: null, dueSoonDays: null }
    const keys = Object.entries(PARAM_KEYS).map(([name, synonyms]) => ({ name: name as keyof Params, synonyms: synonyms.map(synonymTokens) }))

    for (const row of rows) {
        const tokens = tokenize(row.raw('key'))

        if (!tokens.list.length) continue

        let best: { name: keyof Params, score: number } | null = null

        for (const key of keys) {
            for (const synonym of key.synonyms) {
                const score = matchScore(tokens, synonym)

                if (score > 0 && (!best || score > best.score)) best = { name: key.name, score }
            }
        }

        if (!best) continue

        const value = row.raw('value')

        switch (best.name) {
            case 'exchangeRate': {
                const rate = parseNumber(value)

                if (rate && rate > 0) params.exchangeRate = rate < 1 ? 1 / rate : rate
                break
            }
            case 'baseCurrency':
                params.baseCurrency = toCurrency(value)
                break
            case 'cutDate':
                params.cutDate = parseDate(value)
                break
            case 'amberPp':
                params.amberPp = parseNumber(value)
                break
            case 'redPp':
                params.redPp = parseNumber(value)
                break
            case 'dueSoonDays':
                params.dueSoonDays = parseNumber(value)
                break
        }
    }

    return params
}

const buildPortfolio = (rows: Row[]): Initiative[] => uniqueKeys(rows.map(row => {
    const id = row.text('id')
    const name = row.text('name') || id
    const progress = pct(row.pct('progress'))
    const status = toInitiativeStatus(row.raw('status')) ?? inferStatus(progress)

    return {
        key: joinKey(id, name),
        id,
        name,
        ...dimsOf(row),
        status,
        statusRaw: row.text('status'),
        progress,
        planned: pct(row.pct('planned')),
        start: row.date('start'),
        end: row.date('end'),
        tower: row.text('tower'),
        health: toRag(row.raw('health')),
        budget: row.num('budget'),
        notes: row.text('notes'),
        row: row.index
    }
}))

const buildHistory = (rows: Row[]): HistoryRow[] => rows.map(row => {
    const id = row.text('id')
    const name = row.text('name')
    const progress = pct(row.pct('progress'))

    return {
        cut: row.date('cut') as number,
        key: joinKey(id, name),
        id,
        name: name || id,
        ...dimsOf(row),
        progress,
        planned: pct(row.pct('planned')),
        status: toInitiativeStatus(row.raw('status')) ?? (row.has('status') ? null : inferStatus(progress)),
        end: row.date('end'),
        row: row.index
    }
}).filter(item => item.cut !== null && item.key !== '')

const buildCompliance = (rows: Row[]): ComplianceItem[] => uniqueKeys(rows.map(row => {
    const id = row.text('id')
    const requirement = row.text('requirement') || id
    const progress = pct(row.pct('progress'))
    const closed = row.date('closed')
    let status = toComplianceStatus(row.raw('status'))

    if (!status) status = closed ? 'compliant' : progress !== null ? (progress >= 100 ? 'compliant' : progress > 0 ? 'inProgress' : 'pending') : null

    return {
        key: joinKey(id, requirement),
        id,
        requirement,
        framework: row.text('framework'),
        ...dimsOf(row),
        status,
        statusRaw: row.text('status'),
        progress,
        due: row.date('due'),
        closed,
        evidence: row.text('evidence'),
        row: row.index
    }
}))

const buildCsf = (rows: Row[]): CsfItem[] => rows.map(row => ({
    fn: toCsfFunction(row.raw('fn'), row.raw('category')),
    category: row.text('category') || row.text('fn'),
    categoryName: row.text('categoryName'),
    current: row.num('current'),
    target: row.num('target'),
    division: row.text('division'),
    bu: row.text('bu'),
    owner: row.text('owner'),
    period: row.period('period'),
    row: row.index
}))

const buildDesign = (rows: Row[]): DesignActivity[] => uniqueKeys(rows.map(row => {
    const id = row.text('id')
    const activity = row.text('activity') || id
    const progress = pct(row.pct('progress'))

    return {
        key: joinKey(id, activity),
        id,
        activity,
        project: row.text('project'),
        type: row.text('type'),
        ...dimsOf(row),
        status: toInitiativeStatus(row.raw('status')) ?? inferStatus(progress),
        statusRaw: row.text('status'),
        progress,
        start: row.date('start'),
        end: row.date('end'),
        hoursPlanned: row.num('hoursPlanned'),
        hoursActual: row.num('hoursActual'),
        row: row.index
    }
}))

const buildFindings = (rows: Row[]): Finding[] => uniqueKeys(rows.map(row => {
    const id = row.text('id')
    const title = row.text('title') || id
    const closed = row.date('closed')

    return {
        key: joinKey(id, title),
        id,
        title,
        ...dimsOf(row),
        status: toFindingStatus(row.raw('status')) ?? (closed ? 'closed' : 'open'),
        statusRaw: row.text('status'),
        detected: row.date('detected'),
        closed,
        due: row.date('due'),
        asset: row.text('asset'),
        exercise: row.text('exercise'),
        row: row.index
    }
}))

const buildVolumetry = (rows: Row[]): VolumetryRow[] => rows.map(row => ({
    tower: row.text('tower'),
    indicator: row.text('indicator'),
    period: row.period('period') as Period,
    value: row.num('value'),
    target: row.num('target'),
    unit: row.text('unit'),
    direction: toDirection(row.raw('direction')),
    division: row.text('division'),
    bu: row.text('bu'),
    row: row.index
})).filter(item => item.period !== null)

const buildTiers = (rows: Row[]): TierAsset[] => uniqueKeys(rows.map(row => {
    const id = row.text('id')
    const asset = row.text('asset') || id

    return {
        key: joinKey(id, asset),
        id,
        asset,
        ...dimsOf(row),
        tier: toTier(row.raw('tier')),
        tierRaw: row.text('tier'),
        rto: row.text('rto'),
        rpo: row.text('rpo'),
        dataClass: row.text('dataClass'),
        row: row.index
    }
}))

const levelFromScore = (score: number | null) => {
    if (score === null) return null
    if (score >= 15) return 'critical' as const
    if (score >= 10) return 'high' as const
    if (score >= 5) return 'medium' as const

    return 'low' as const
}

const buildRisks = (rows: Row[]): Risk[] => uniqueKeys(rows.map(row => {
    const id = row.text('id')
    const risk = row.text('risk') || id
    const probability = toScale5(row.raw('probability'))
    const impact = toScale5(row.raw('impact'))
    const score = probability !== null && impact !== null ? probability * impact : row.num('score')
    const dims = dimsOf(row)

    return {
        key: joinKey(id, risk),
        id,
        risk,
        ...dims,
        criticality: dims.criticality ?? levelFromScore(score),
        probability,
        impact,
        score,
        status: toRiskStatus(row.raw('status')) ?? (row.has('status') ? null : 'open'),
        statusRaw: row.text('status'),
        treatment: row.text('treatment'),
        review: row.date('review'),
        trend: row.text('trend'),
        row: row.index
    }
}))

const buildBudget = (rows: Row[], params: Params): BudgetRow[] => rows.map(row => {
    const lineId = row.text('lineId')
    const line = row.text('line') || lineId
    const program = row.text('program')
    const category = row.text('category')
    const bu = row.text('bu')
    const lineKey = normalizeValue(lineId) ? `id:${normalizeValue(lineId)}` : `ln:${normalizeValue([line, program, category, bu].join(' | '))}`

    return {
        row: row.index,
        lineKey,
        lineId,
        line,
        category,
        ...dimsOf(row),
        program,
        bu,
        period: row.period('period'),
        plan: row.num('plan'),
        actual: row.num('actual'),
        committed: row.num('committed'),
        currency: toCurrency(row.raw('currency')) ?? params.baseCurrency ?? 'MXN',
        vendor: row.text('vendor')
    }
})

const buildAgreements = (rows: Row[]): Agreement[] => uniqueKeys(rows.map(row => {
    const id = row.text('id')
    const agreement = row.text('agreement') || id
    const closed = row.date('closed')

    return {
        key: joinKey(id, agreement),
        id,
        agreement,
        forum: row.text('forum'),
        ...dimsOf(row),
        status: toAgreementStatus(row.raw('status')) ?? (closed ? 'done' : 'pending'),
        statusRaw: row.text('status'),
        date: row.date('date'),
        due: row.date('due'),
        closed,
        row: row.index
    }
}))

const buildBasics = (rows: Row[]): BasicControl[] => {
    const items = rows.map(row => ({
        control: row.text('control'),
        value: row.num('value'),
        target: row.num('target'),
        unit: row.text('unit'),
        unitIsPercent: row.text('unit').includes('%') || ['pct', 'porcentaje', 'percent'].includes(normalizeValue(row.raw('unit'))),
        textPercent: isPercentText(row.raw('value')),
        isPercent: false,
        direction: toDirection(row.raw('direction')),
        division: row.text('division'),
        bu: row.text('bu'),
        owner: row.text('owner'),
        domain: row.text('domain'),
        period: row.period('period'),
        row: row.index
    }))

    // Coverage written as fractions (0.92) is shown as 92 %
    const numeric = items.filter(item => !item.textPercent && item.value !== null)
    const fractions = numeric.length > 0 && numeric.every(item => Math.abs(item.value as number) <= 1 && (item.target === null || Math.abs(item.target) <= 1))

    return items.map(({ unitIsPercent, textPercent, ...item }) => {
        const scale = fractions && !textPercent && (unitIsPercent || !item.unit)
        const isPercent = unitIsPercent || textPercent || scale

        return {
            ...item,
            value: scale && item.value !== null ? item.value * 100 : item.value,
            target: scale && item.target !== null ? item.target * 100 : item.target,
            isPercent,
            direction: item.direction ?? 'up'
        }
    })
}

/* ------------------------------------------------------------------ */
/* Quality report                                                      */
/* ------------------------------------------------------------------ */

const reportFor = (def: SheetDef, sheet: SheetRows | null, valid: Row[]): SheetReport => {
    if (!sheet) {
        return {
            key: def.key,
            required: def.required,
            recognized: false,
            sheetName: null,
            rowsRead: 0,
            rowsValid: 0,
            rowsDiscarded: 0,
            fieldsFound: [],
            fieldsMissing: def.fields.filter(field => field.important).map(field => field.key),
            completeness: []
        }
    }

    const found = def.fields.filter(field => sheet.mapping.has(field.key)).map(field => field.key)

    return {
        key: def.key,
        required: def.required,
        recognized: true,
        sheetName: sheet.sheetName,
        rowsRead: sheet.rowsRead,
        rowsValid: valid.length,
        rowsDiscarded: sheet.rowsRead - valid.length,
        fieldsFound: found,
        fieldsMissing: def.fields.filter(field => field.important && !sheet.mapping.has(field.key)).map(field => field.key),
        completeness: def.fields
            .filter(field => field.important && sheet.mapping.has(field.key))
            .map(field => ({ field: field.key, filled: valid.filter(row => hasValue(row, field.key)).length, total: valid.length }))
    }
}

/* ------------------------------------------------------------------ */
/* Entry point                                                         */
/* ------------------------------------------------------------------ */

const startsWithZipSignature = (bytes: Uint8Array): boolean => {
    return bytes.length > 4 && bytes[0] === 0x50 && bytes[1] === 0x4b && bytes[2] === 0x03 && bytes[3] === 0x04
}

/**
 * Validates and reads a workbook entirely inside the browser.
 * Returns `{ ok: false }` on any problem, without saying which check failed.
 */
export const loadWorkbook = async (file: File): Promise<LoadResult> => {
    try {
        if (!file || !/\.xlsx$/i.test(file.name)) return FAIL
        if (file.size <= 0 || file.size > MAX_BYTES) return FAIL
        if (!ALLOWED_TYPES.includes(file.type)) return FAIL

        const bytes = new Uint8Array(await file.arrayBuffer())

        if (!startsWithZipSignature(bytes)) return FAIL

        const XLSX = await import('xlsx')
        const workbook = XLSX.read(bytes, {
            type: 'array',
            cellFormula: false,
            cellHTML: false,
            cellStyles: false,
            cellText: false,
            // Number formats are kept so an exported copy still shows dates as dates
            cellNF: true,
            cellDates: false,
            bookVBA: true,
            bookFiles: true,
            sheetRows: MAX_ROWS + 2
        })

        const internals = workbook as WorkBook & { bookType?: string, keys?: string[], files?: unknown, cfb?: unknown }

        if (internals.bookType !== 'xlsx') return FAIL
        if (workbook.vbaraw) return FAIL
        if ((internals.keys ?? []).some(part => FORBIDDEN_PARTS.some(pattern => pattern.test(part)))) return FAIL

        delete internals.keys
        delete internals.files
        delete internals.cfb

        const names = workbook.SheetNames ?? []

        if (names.length < 1 || names.length > MAX_SHEETS) return FAIL

        const date1904 = Boolean(workbook.Workbook?.WBProps?.date1904)
        const tables: Table[] = []

        for (const name of names) {
            const sheet = workbook.Sheets[name] as WorkSheet | undefined

            if (!sheet) continue

            // Real used range, from the cells that hold a value. The declared range
            // can be inflated by formatting and is not trusted.
            let minRow = Number.POSITIVE_INFINITY
            let minCol = Number.POSITIVE_INFINITY
            let maxRow = -1
            let maxCol = -1

            for (const address of Object.keys(sheet)) {
                if (address.startsWith('!')) continue

                const cell = sheet[address] as CellObject | undefined

                if (!cell || cell.v === undefined || cell.v === null || cell.v === '') continue

                const { r, c } = XLSX.utils.decode_cell(address)

                if (r < minRow) minRow = r
                if (c < minCol) minCol = c
                if (r > maxRow) maxRow = r
                if (c > maxCol) maxCol = c
            }

            if (maxRow < 0) continue

            // Reaching the parse cap means the sheet was cut: it is too large
            if (maxRow >= MAX_ROWS + 1 || maxRow - minRow + 1 > MAX_ROWS + 1) return FAIL
            if (maxCol - minCol + 1 > MAX_COLS) return FAIL

            const range = { s: { r: minRow, c: minCol }, e: { r: maxRow, c: maxCol } }

            sheet['!ref'] = XLSX.utils.encode_range(range)

            // Blank rows are kept so every row index maps back to its cell in the sheet
            const aoa = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, raw: true, defval: null, blankrows: true })
            const head = aoa.slice(0, HEADER_SCAN_ROWS).map(cells => (Array.isArray(cells) ? cells : []).map(tokenize))

            tables.push({ name, aoa, head, origin: range.s })
        }

        /* Assign workbook sheets to known sheet types: name first, columns as tie-breaker or fallback */
        const candidates: { table: number, def: SheetDef, score: number }[] = []

        tables.forEach((table, index) => {
            const sheetName = tokenize(table.name)
            const byName = SHEETS.map(def => nameScore(def, sheetName))
            const anyName = byName.some(score => score >= 50)

            SHEETS.forEach((def, defIndex) => {
                const named = byName[defIndex] >= 50

                if (anyName && !named) return

                const byColumns = fingerprintScore(def, table)
                const minimum = Math.min(3, def.fingerprint.length)

                if (named) candidates.push({ table: index, def, score: byName[defIndex] + byColumns * 5 })
                else if (byColumns >= minimum) candidates.push({ table: index, def, score: 20 + byColumns * 5 })
            })
        })

        candidates.sort((a, b) => b.score - a.score || a.table - b.table)

        const assigned = new Map<SheetKey, number>()
        const usedTables = new Set<number>()

        for (const candidate of candidates) {
            if (assigned.has(candidate.def.key) || usedTables.has(candidate.table)) continue

            assigned.set(candidate.def.key, candidate.table)
            usedTables.add(candidate.table)
        }

        const sheets = new Map<SheetKey, SheetRows>()

        for (const def of SHEETS) {
            const tableIndex = assigned.get(def.key)

            if (tableIndex === undefined) continue

            const table = tables[tableIndex]
            const header = findHeader(def, table)

            if (!header) continue

            sheets.set(def.key, buildRows(def, table.name, table.aoa, header.row, header.mapping, date1904))
        }

        const valid = new Map<SheetKey, Row[]>()

        for (const [key, sheet] of sheets) valid.set(key, validRows(sheet))

        const rowsOf = (key: SheetKey): Row[] => valid.get(key) ?? []

        const params = buildParams(rowsOf('params'))
        const portfolio = buildPortfolio(rowsOf('portfolio'))

        // The current portfolio is the one sheet the dashboard cannot work without
        if (!sheets.has('portfolio') || portfolio.length === 0) return FAIL

        const budget = buildBudget(rowsOf('budget'), params)

        if (params.exchangeRate === null && sheets.get('budget')?.mapping.has('rate')) {
            const rates = rowsOf('budget').map(row => row.num('rate')).filter((rate): rate is number => rate !== null && rate > 0)

            if (rates.length) {
                const average = rates.reduce((sum, rate) => sum + rate, 0) / rates.length

                params.exchangeRate = average < 1 ? 1 / average : average
            }
        }

        const budgetSheet = sheets.get('budget')
        const budgetOrigin = tables.find(table => table.name === budgetSheet?.sheetName)?.origin ?? { r: 0, c: 0 }
        const budgetSource: BudgetSource | null = budgetSheet
            ? {
                sheetName: budgetSheet.sheetName,
                aoa: budgetSheet.aoa,
                headerRow: budgetSheet.headerRow,
                originRow: budgetOrigin.r,
                originCol: budgetOrigin.c,
                colPlan: budgetSheet.mapping.get('plan') ?? null,
                colActual: budgetSheet.mapping.get('actual') ?? null
            }
            : null

        const fields = Object.fromEntries(SHEETS.map(def => [def.key, [...(sheets.get(def.key)?.mapping.keys() ?? [])]])) as Record<SheetKey, string[]>

        const quality: QualityReport = {
            fileName: cleanText(file.name),
            fileSize: file.size,
            loadedAt: Date.now(),
            totalSheets: names.length,
            sheets: SHEETS.map(def => reportFor(def, sheets.get(def.key) ?? null, rowsOf(def.key))),
            unrecognized: tables.filter((_, index) => !usedTables.has(index)).map(table => cleanText(table.name))
        }

        const dataset: Dataset = {
            portfolio,
            history: buildHistory(rowsOf('history')),
            compliance: buildCompliance(rowsOf('compliance')),
            csf: buildCsf(rowsOf('csf')),
            design: buildDesign(rowsOf('design')),
            findings: buildFindings(rowsOf('findings')),
            volumetry: buildVolumetry(rowsOf('volumetry')),
            tiers: buildTiers(rowsOf('tiers')),
            risks: buildRisks(rowsOf('risks')),
            budget,
            agreements: buildAgreements(rowsOf('agreements')),
            basics: buildBasics(rowsOf('basics')),
            params,
            fields,
            budgetSource,
            quality
        }

        return { ok: true, dataset, workbook }
    }
    catch {
        return FAIL
    }
}
